import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import * as chroma from 'chroma.ts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexNoData,
  ApexPlotOptions,
  ApexStates,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent
} from 'ng-apexcharts';
import { debounceTime, first, take } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { InlineSvgComponent } from '../../../components/inline-svg/inline-svg.component';
import { Layer } from '../../../models/layer.models';
import { LayerData } from '../../../models/report.model';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { LayerService } from '../../../services/layer.service';
import { NewReportService } from '../../../services/newReport.service';
import { NotificationService } from '../../../services/notification.service';
import {
  closestUpperDivisibleBy10,
  formatLegend,
  getDiscreteColors,
  getGradientBackground, getLayerLegends,
  getSpacedNumericLegends, isLinearLayer,
  isNumeric
} from '../../../utils';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  title: ApexTitleSubtitle;
  noData: ApexNoData;
  tooltip: ApexTooltip;
  states?: ApexStates;
};

@Component({
  selector: 'frmg-report-area-details',
  templateUrl: './report-area-details-modal.component.html',
  styleUrls: ['./report-area-details-modal.component.scss'],
  imports: [
    NgClass,
    NgTemplateOutlet,
    MatTooltip,
    InlineSvgComponent,
    ChartComponent,
    TranslateModule
  ],
  providers: [MatDialog]
})
export class ReportAreaDetailsComponent implements OnInit {
  @ViewChild('chart') chart: ChartComponent;
  @ViewChild('chartFire') chartFire: ChartComponent;
  @ViewChild('chartAPX') chartAPX: ApexChart;
  @ViewChild('chartAPXExp') chartAPXExp: ApexChart;
  public chartOptions: Partial<ChartOptions>;
  public chartFireOptions: Partial<ChartOptions>;

  @Input() detailsId!: string;
  @Input() id!: string;
  @Input() collectionId!: string;

  public data: any = {};
  public exposureLayerGradient: string;
  public lineLength: string;
  public fullImgDialogRef: MatDialogRef<any>;
  public layer_data: LayerData[] = [];
  public layer: LayerData;
  public mapLayer: Layer;
  public isPoint: boolean;
  private mapLayers: Array<Layer>;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private reportService: NewReportService,
    private layerService: LayerService,
    private notificationService: NotificationService,
    private errorHandlerService: ErrorHandlerService,
    public dialog: MatDialog
  ) {
    this.chartOptions = {
      series: [],
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        height: '100%',
        width: '100%',
        events: {},
        zoom: {
          enabled: false
        },
        redrawOnParentResize: true,
        animations: {
          enabled: false
        }
      },
      fill: {
        colors: ['#5E84F7'],
        opacity: 1,
        type: 'gradient',
        gradient: {
          gradientToColors: ['#547BF2'],
          inverseColors: true
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          borderRadiusApplication: 'end',
          columnWidth: '90%'
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200],
        tickPlacement: 'on',
        labels: {
          style: {
            fontFamily: 'Montserrat, sans-serif'
          }
        },
        title: {
          text: 'Value',
          offsetX: 0,
          style: {
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Montserrat, sans-serif'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            fontFamily: 'Montserrat, sans-serif'
          },
          formatter(val: number): string | string[] {
            if (val / 1000 > 1) {
              return `${(val / 1000).toFixed(0)}k`;
            }
            return `${val}`;
          }
        },
        title: {
          text: 'Percentage (%)',
          offsetX: -7,
          style: {
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Montserrat, sans-serif'
          }
        }
      },
      noData: {
        text: 'Loading...'
      },
      tooltip: {
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const initData = series[seriesIndex][dataPointIndex];
          return `<div style="width: 100%; height: 100%; padding: 10px 12px;background: #F7F9F9; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18); border-radius: 4px; display: flex">
            <div style="color: #272727; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 500;">${
  w.globals.categoryLabels[dataPointIndex]
    ? w.globals.categoryLabels[dataPointIndex].join
      ? w.globals.categoryLabels[dataPointIndex].join(' ')
      : w.globals.categoryLabels[dataPointIndex]
    : `${this.layer?.histogram?.bin_edges[dataPointIndex]} - ${
      this.layer?.histogram?.bin_edges[dataPointIndex + 1] ||
                    this.layer?.histogram?.max
    }`
}:</div>
            <div style="color: #5E84F7; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 600; margin-left: 8px">${initData} (%)</div>
           </div>`;
        }
      },
      states: {
        active: {
          filter: {
            type: 'none' /* none, lighten, darken */
          }
        },
        hover: {
          filter: {
            type: 'none'
          }
        }
      }
    };
    this.chartFireOptions = {
      series: [],
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        height: '100%',
        events: {},
        zoom: {
          enabled: false
        },
        redrawOnParentResize: true
      },
      plotOptions: {},
      dataLabels: {
        enabled: false
      },
      xaxis: {
        tickAmount: 6,
        labels: {
          show: true,
          maxHeight: 60,
          hideOverlappingLabels: true,
          style: {
            fontFamily: 'Montserrat, sans-serif'
          }
        },
        tooltip: {
          enabled: false
        },
        title: {
          text: 'Year',
          style: {
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Montserrat, sans-serif'
          }
        }
      },
      yaxis: {
        tickAmount: 5,
        decimalsInFloat: 0,
        labels: {
          style: {
            fontFamily: 'Montserrat, sans-serif'
          }
        },
        title: {
          text: 'Hectares Burned',
          offsetX: -7,
          style: {
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Montserrat, sans-serif'
          }
        }
      },
      fill: {
        colors: ['#5E84F7'],
        opacity: 1,
        type: 'gradient',
        gradient: {
          gradientToColors: ['#547BF2'],
          inverseColors: true
        }
      },
      noData: {
        text: 'Loading...'
      },
      tooltip: {
        enabled: true,
        custom(x) {
          const initData = x.w.globals.initialSeries[0].data[x.dataPointIndex];
          return `<div style="width: 100%; height: 100%; padding: 10px 12px;background: #F7F9F9; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18); border-radius: 4px; display: flex">
            <div style="color: #272727; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 500;">${
  initData.x
}:</div>
            <div style="color: #5E84F7; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 600; margin-left: 16px">${initData.y.toFixed(
    0
  )}</div>
           </div>`;
        }
      }
    };
  }

  ngOnInit(): void {
    this.reportService.getReportCollectionGeometryLayerData(this.id, this.collectionId, this.detailsId).subscribe((resp) => {
      console.log(resp);
    });
    this.layerService
      .getLayers()
      .pipe(debounceTime(0), take(1))
      .subscribe((resp) => {
        this.mapLayers = resp;
        const exposureLayer = resp.find(
          (layer) => layer.layer.name.toLowerCase() === 'exposure'
        );
        const colors = Object.entries(exposureLayer.layer.colormap_values)
          .map(([key, value]) => `${value} ${+key / 2}%`)
          .join(', ');
        this.exposureLayerGradient = `linear-gradient(to right, ${colors})`;

        this.reportService.selectedLayer
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((name) => {
            const layer = this.layer_data.find(
              (layer) => layer.layer_name === name
            );
            name = this.layerLabelTransform(name);
            if (!layer) return;
            this.mapLayer = this.mapLayers.find(
              (layer) =>
                layer.layer.name?.toLowerCase() === name.toLowerCase() ||
                layer.layer.name?.toLowerCase() === name.toLowerCase()
            );
            this.layer = layer;

            const data = {
              image: layer.image_path,
              input_point_value: layer.point_value,
              threshold_percentage: 0,
              value_text:
                layer.histogram?.avg_value?.toFixed(0) || layer.value_text,
              majority_value:
                layer.layer_name === 'Land Cover'
                  ? layer.histogram.majority_value - 1
                  : layer.histogram.majority_value
            };

            let labels = layer.histogram?.bin_labels;

            this.chartOptions.plotOptions = {
              bar: {
                borderRadius: 5,
                borderRadiusApplication: 'end',
                columnWidth:
                  isLinearLayer(this.mapLayer) ? '90%' : '100%'
              }
            };

            if (isLinearLayer(this.mapLayer)) {
              labels.forEach((label, index) => {
                if (
                  getLayerLegends(this.mapLayer)[
                    layer.layer_name === 'Land Cover' ? label - 1 : label
                  ]
                ) {
                  labels[index] =
                    getLayerLegends(this.mapLayer)[
                      layer.layer_name === 'Land Cover' ? label - 1 : label
                    ];
                }
              });
            } else {
              labels = layer.histogram?.bin_edges;
            }
            const rawData = layer.histogram?.bin_percentages?.map(
              (data) => +data.toFixed(1)
            );

            const newData = Array.from({ length: rawData?.length }, (_, i) => ({
              x: i + 0.5,
              y: rawData[i]
            }));
            newData.push({ x: newData.length + 0.5, y: 0 });
            if (!isLinearLayer(this.mapLayer)) {
              const max = Math.max(...newData.map((xy) => xy.y));
              const closest = closestUpperDivisibleBy10(max);
              const maxY = closest === max ? max : closest;
              const yaxis: Partial<ApexYAxis> = {
                decimalsInFloat: 0,
                labels: {
                  style: {
                    fontFamily: 'Montserrat, sans-serif'
                  }
                },
                title: {
                  text: 'Percentage (%)',
                  offsetX: -7,
                  style: {
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Montserrat, sans-serif'
                  }
                }
              };
              if (maxY) {
                yaxis.max = maxY;
                yaxis.min = 0;
                // yaxis.tickAmount = maxY/10;
                if (max === 100)
                  yaxis.labels.formatter = function(
                    val: number
                  ): string | string[] {
                    return val > max
                      ? ''
                      : val === 100
                        ? ['', '100']
                        : `${val}`;
                  };
              }
              this.chartOptions.yaxis = yaxis;
            } else {
              this.chartOptions.yaxis = {
                decimalsInFloat: 0,
                labels: {
                  style: {
                    fontFamily: 'Montserrat, sans-serif'
                  }
                },
                title: {
                  text: 'Percentage (%)',
                  offsetX: -7,
                  style: {
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Montserrat, sans-serif'
                  }
                }
              };
            }
            if (this.mapLayer.layer.name === 'landcover') rawData.pop();

            this.chartOptions.series = [
              {
                name: 'histogram_data',
                data:
                  isLinearLayer(this.mapLayer)
                    ? rawData
                    : newData
              }
            ];
            const labelsInfo = {
              style: {
                fontFamily: 'Montserrat, sans-serif'
              }
            };
            if (this.mapLayer.layer.name === 'Land Cover') {
              // @ts-ignore
              labelsInfo.rotate = -45;
              // @ts-ignore
              labelsInfo.rotateAlways = true;
              // @ts-ignore
              labelsInfo.formatter = (x) => {
                return x?.split(' ');
              };
            }
            if (!isLinearLayer(layer.layerInfo)) {
              // @ts-ignore
              labelsInfo.formatter = (x) => {
                return labels[x];
              };
            }

            this.chartOptions.xaxis = {
              categories:
                isLinearLayer(layer.layerInfo) ? labels : [],
              tickAmount:
                isLinearLayer(layer.layerInfo)
                  ? labels.length
                  : this.chartOptions.series[0].data.length,
              // min: 0,
              max:
                isLinearLayer(layer.layerInfo)
                  ? labels.length
                  : this.chartOptions.series[0].data.length,
              tickPlacement: 'on',
              labels: labelsInfo,
              // @ts-ignore
              stepSize: 1,
              title: {
                text: `${this.layerLabelTransform(layer?.layer_name)}${
                  this.mapLayer.layer.unit
                    ? ` (${this.mapLayer.layer.unit})`
                    : ''
                }`,
                offsetX: 0,
                style: {
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Montserrat, sans-serif'
                }
              }
            };
            // if (this.mapLayer.layer_name === 'landcover') this.chartOptions.xaxis.categories.pop()
            if (isLinearLayer(layer.layerInfo)) {
              this.chartOptions.xaxis.min = 0;
            }
            this.chartOptions.fill = {
              colors: this.getFillColors(
                this.mapLayer,
                layer.histogram?.bin_labels?.length,
                layer.histogram
              ),
              opacity: 1
            };
            this.data = { ...this.data, ...data };
          });
        this.reportService
          .getReportDetails(this.id, this.collectionId, this.detailsId)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            catchError(
              this.errorHandlerService.handle404And422(`report/${this.id}`)
            )
          )
          .subscribe((resp) => {
            this.layer_data = resp.layer_data.map((layerData) => {
              const layer = this.mapLayers.find(
                (layerObj) => layerObj.layer.name === layerData.layer.name
              );
              layerData.layerInfo = layer;
              return layerData;
            });
            this.reportService.detailsLayerData.next(this.layer_data);
            if (typeof resp.fire_trends_data === 'string') {
              resp.fire_trends_data = JSON.parse(resp.fire_trends_data);
            }
            this.chartFireOptions.series = [
              {
                name: 'fire_data',
                data: resp.fire_trends_data?.data?.map((data, index) => {
                  return { x: resp.fire_trends_data.labels[index], y: data };
                })
              }
            ];
            this.data = {
              ...this.data,
              ...resp,
              ...{ threshold_percentage: 0, input_point_value: 0 }
            };
            this.lineLength = Math.abs(
              (this.data.from_distance - this.data.to_distance) / 1000
            ).toFixed(0);
            // this.reportService.selectedLayer.next(this.layer_data[0].layer_name);
            this.reportService
              .getReport(this.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((report) => {
                this.data = { ...report, ...this.data };
                this.isPoint =
                  report.area_collections?.find(
                    (coll) => coll.id === this.collectionId
                  )?.geometry_json?.type === 'Point';
                const collection = this.data.area_collections?.find(
                  (coll) => coll.id === this.data.area_collection_id
                );
                const notLine = collection.geometry_json.type !== 'LineString';
                if (notLine) {
                  this.reportService.breadCrumbs.next([
                    ...this.reportService.breadCrumbs.getValue(),
                    collection.properties.name || 'Details'
                  ]);
                } else {
                  this.reportService
                    .getReportAnalyseById(this.id, this.collectionId)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((analysis) => {
                      // @ts-ignore
                      const segment = analysis.area_details.find(
                        (segment) => segment.id === this.data.id
                      );
                      this.reportService.breadCrumbs
                        .pipe(first())
                        .subscribe((resp) => {
                          if (resp.length === 0) {
                            this.reportService.breadCrumbs.next([
                              ...this.reportService.breadCrumbs.getValue(),
                              analysis.collection_metadata.area_name
                            ]);
                          }
                          this.reportService.breadCrumbs.next([
                            ...this.reportService.breadCrumbs.getValue(),
                            `Segement ${segment.segment_index + 1}`
                          ]);
                        });
                    });
                }
              });
          });
      });
  }

  public calculateThreshold(layer: Layer) {
    if (!this.data.config) return ['Loading'];
    const config = this.data.config.layers.find(
      (conf) =>
        this.layerLabelTransform(conf?.name) ==
        this.layerLabelTransform(layer?.layer.name)
    );
    if (config?.selected_values?.length) {
      return config.selected_values.map(
        (value) =>
          config.value_descriptions[
            config.name === 'Land Cover' ? value + 1 : value
          ]
      );
    } else {
      return `${config.min_threshold} - ${config.max_threshold}`;
    }
  }

  public calculateExposureValue() {
    const config = this.layer_data.find(
      (conf) => conf?.layer_name == 'Exposure'
    );
    if (config) {
      return +config.value_text.toFixed
        ? +config.value_text.toFixed(0)
        : config.value_text;
    } else {
      return 'Loading';
    }
  }

  public calculateValueLabel(value: any, layer: Layer) {
    if (!isLinearLayer(layer)) {
      if (layer.layer.name !== 'fuelhazard') {
        return getLayerLegends(layer)[value.majority_value];
      }
      return getLayerLegends(layer)[value.value_text];
    } else {
      return value.value_text || 0;
    }
  }

  public selectionLabel(layer: Layer) {
    if (!isLinearLayer(layer)) {
      return 'Selections of Map';
    } else {
      return 'Threshold Range';
    }
  }

  public createExposureExplanation(layerName: string): string {
    const type = this.data.area_collections?.find(
      (el) => el.id === this.data.area_collection_id
    ).geometry_json.type;
    let prefix = '';
    if (type === 'LineString' && layerName === 'Exposure') {
      prefix = '(Along The Line)';
    } else if (
      (type === 'Polygon' || type === 'MultiPolygon') &&
      layerName === 'Exposure'
    ) {
      prefix = '(Inside The Polygon)';
    } else {
      prefix = '(Within 4km Buffer)';
    }
    return prefix;
  }

  private getFillColors(layer: Layer, _: number, histogram: any) {
    const scale = chroma.scale(
      this.getDiscreteColors(layer)
    );
    if (!isLinearLayer(layer)) {
      return [
        (value) => {
          return this.getDiscreteColors(layer)[
            value.dataPointIndex
          ];
        }
      ];
    } else {
      return [
        function(value) {
          const max = Math.max(
            ...getLayerLegends(layer).map((str) =>
              parseFloat(str.replace(',', '.').replace(' ', ''))
            )
          );
          return scale(
            histogram.bin_edges[value.dataPointIndex] / max
          ).toString();
        }
      ];
    }
  }

  public downloadArea(event: Event): void {
    event.stopPropagation();
    const id = this.detailsId;
    const filename = this.data.analysis.name;
    this.reportService
      .getAreaReportPdf(this.data.report.id, id, filename)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.notificationService.updateDownload({
          fileName: `${filename}.xlsx`,
          percentage: data.progress,
          total: data.total,
          loaded: data.loaded
        });
      });
  }

  public openImage(templateRef) {
    this.fullImgDialogRef = this.dialog.open(templateRef, {
      width: '85vh',
      height: '85vh'
    });
  }

  public colapseImage() {
    this.fullImgDialogRef?.close();
  }

  public downloadAreaXLSX(event: Event): void {
    event.stopPropagation();
    const id = this.data.analysis.id;
    const filename = this.data.analysis.name;
    this.reportService
      .getAreaReportXLSX(this.data.report.id, id, filename)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.notificationService.updateDownload({
          fileName: `${filename}.xlsx`,
          percentage: data.progress,
          total: data.total,
          loaded: data.loaded
        });
      });
  }

  public layerLabelTransform(name: string) {
    return name?.replace(' Model', '')?.replace('PSBA', 'PCBA');
  }

  public layerValuePrefix(layer: Layer) {
    if (
      isLinearLayer(layer) &&
      layer.layer.name !== 'fuelhazard'
    ) {
      return 'Majority ';
    }
    return 'Average ';
  }

  //
  // public closeDialog(result?: any): void {
  //   this.dialogRef.close(result);
  // }

  protected readonly getGradientBackground = getGradientBackground;
  protected readonly getDiscreteColors = getDiscreteColors;
  protected readonly isNumeric = isNumeric;
  protected readonly getSpacedNumericLegends = getSpacedNumericLegends;
  protected readonly formatLegend = formatLegend;
  protected readonly isLinearLayer = isLinearLayer;
  protected readonly getLayerLegends = getLayerLegends;
}
